/**
 * Cost sheet model and formulas.
 * Since 偏差 D8 the quote coefficients follow the company cost-sheet template and live on each sheet (sheet.qcoef);
 * a sheet without qcoef (made before D8, or by the original tool) keeps the v2 formulas via LEGACY_QCOEF.
 */
import { f2, n } from "./num.ts";
import type { AllCmParts, AddKey, CmMore, CmPartKey, CmParts, ExtraKey, Header, LineKind, LineRow, Per, QCoef, Sheet, Upcharge } from "./types.ts";

/**
 * Company template defaults (JF 2026-09-29): 6% finance in BOTH the actual and the quoted price of fabrics and trims,
 * +5% on every quoted price and yield, CM × 1.7, 20% duty.
 */
export const DEFAULT_QCOEF: QCoef = { finActual: 0.06, fin: 0.06, fabPx: 0.05, fabYld: 0.05, trimPx: 0.05, trimYld: 0.05, add: 0.05, cmMult: 1.7, tariff: 20 };
/** The v2 single-file tool: 6% finance inside the actual cost, price +5%, yield fabric +10% / trim +5%, CM unchanged. */
export const LEGACY_QCOEF: QCoef = { finActual: 0.06, fin: 0.06, fabPx: 0.05, fabYld: 0.1, trimPx: 0.05, trimYld: 0.05, add: 0.05, cmMult: 1 };

/** v2 constants, kept for reference / old code paths. */
export const FIN = LEGACY_QCOEF.finActual;
export const Q_PX = 1 + LEGACY_QCOEF.fabPx;
export const Q_YLD = { fab: 1 + LEGACY_QCOEF.fabYld, trim: 1 + LEGACY_QCOEF.trimYld } as const;
export const Q_ADD = 1 + LEGACY_QCOEF.add;

export const coefOf = (s: Pick<Sheet, "qcoef">): QCoef => s.qcoef || LEGACY_QCOEF;

export const ADD: [AddKey, string, string][] = [
  ["wash", "Garment wash", "洗水"],
  ["emb", "Embroidery / print", "绣花 / 印花"],
  ["trans", "Transportation fee", "运输费"],
  ["sample", "Sample charge", "样品费"],
  ["handle", "Handle cost", "Handle cost"],
  ["drop", "Drop shipment", "Drop shipment"],
];

/* ---------------- currency (JF 2026-10-13) ---------------- */

/** Default RMB per USD (JF 2026-10-13: "6.71 — where would you get 7.2 now?"); the company default is set in the admin page. */
export const DEFAULT_FX = 6.71;
export const fxOf = (s: Pick<Sheet, "fx">, d = DEFAULT_FX): number => (s.fx && s.fx > 0 ? s.fx : d);
/** An RMB amount in USD, kept to 6 decimals (the sheet stores USD; the RMB figure is kept beside it). */
export const fromCny = (cny: unknown, fx: number): string => (isSet(cny) ? String(+(n(cny) / fx).toFixed(6)) : "");
/** What to store for an amount typed in the given currency: px in USD, and the RMB figure when typed in RMB. */
export function moneyIn(typed: string, cur: "USD" | "CNY", fx: number): { px: string; cny?: string } {
  if (cur === "USD" || !isSet(typed.trim())) return { px: typed };
  return { px: fromCny(typed, fx), cny: typed };
}

/* ---------------- CM = everything paid to the garment factory ---------------- */

/**
 * CM parts (JF 2026-10-13: "the CM is now what we pay the factory in total" — split it out). Washing / embroidery can be
 * moved to the Additional rows of the same key (paid by Lever Style). "cmt" (cut-make-pack in one figure) only shows on
 * sheets made before the split.
 */
export const CM_PARTS: [CmPartKey, string, string][] = [
  ["cut", "Cutting", "裁剪"],
  ["sew", "Sewing", "车缝"],
  ["pack", "Packing / finishing", "包装 / 后整"],
  ["wash", "Garment wash", "洗水"],
  ["emb", "Embroidery / print", "绣花 / 印花"],
  ["other", "Other special process", "其他特殊工艺"],
  ["docs", "Customs & document fees", "报关 / 文件费"],
  ["cmt", "Cut, make & pack", "裁剪车缝包装"],
];
/** The per-piece parts a tiered quote keeps per quantity (customs & documents are per order: the quantity spreads them). */
export const TIER_PARTS: CmPartKey[] = ["cut", "sew", "pack", "wash", "emb", "other", "cmt"];
/** Per piece, or a whole-order amount spread over the quantity; customs & documents default to the whole order. */
export const partPer = (k: string, p: { per?: Per }): Per => p.per || (k === "docs" ? "order" : "pc");

// Customs & document fees: no fixed amount any more (JF 2026-10-16 took back 2026-10-13's ¥3,000 per order — one shipment
// carries many orders and pays it once). The row stays, empty, for the AM to fill in by hand.

/** The CM parts of a sheet; a sheet without parts has its whole CM as cut-make-pack. */
export function cmPartsOf(s: Pick<Sheet, "cm">): AllCmParts {
  const p = {} as AllCmParts;
  for (const [k] of CM_PARTS) p[k] = { px: "", desc: "" };
  if (s.cm.parts) { for (const [k] of CM_PARTS) if (s.cm.parts[k]) p[k] = { ...s.cm.parts[k] }; }
  else p.cmt.px = s.cm.v;
  return p;
}

/** Set the CM parts and the total (their per-piece sum at the sheet's quantity). Mutates. */
export function setCmParts(s: Sheet, p: CmParts): void {
  s.cm.parts = p;
  const lines = cmLines(s);
  s.cm.v = lines.some((l) => isSet(l.px)) ? f2(lines.reduce((a, l) => a + l.actual, 0)) : "";
}

/** Every CM row — the parts and the rows the AM added — per piece, with the factor each is quoted at (JF 2026-10-13). */
export interface CmLine {
  key: string; part?: CmPartKey; en: string; zh: string; name?: string; desc: string; px: string; mult?: string; factor: number;
  per: Per; cny?: string; /** per piece at the sheet's quantity */ actual: number;
}
export function cmLines(s: Pick<Sheet, "cm" | "qcoef" | "h">, c: QCoef = coefOf(s)): CmLine[] {
  const p = cmPartsOf(s);
  const qty = n(s.h.qty);
  const factor = (m: string | undefined) => (isSet(m) ? n(m) : c.cmMult);
  const out: CmLine[] = CM_PARTS
    .filter(([k]) => k !== "cmt" || isSet(p.cmt.px) || !!p.cmt.desc)
    .map(([k, en, zh]) => {
      const per = partPer(k, p[k]);
      return { key: k, part: k, en, zh, desc: p[k].desc, px: p[k].px, mult: p[k].mult, factor: factor(p[k].mult), per, cny: p[k].cny, actual: perPiece(p[k].px, per, qty) };
    });
  (s.cm.more || []).forEach((m: CmMore, i) => {
    const per = m.per || "pc";
    out.push({ key: "more" + i, en: m.name, zh: m.name, name: m.name, desc: m.desc, px: m.px, mult: m.mult, factor: factor(m.mult), per, cny: m.cny, actual: perPiece(m.px, per, qty) });
  });
  return out;
}

/**
 * Cutting / sewing / packing (JF 2026-10-13) from the AI's split of its cut-make-pack figure. `cm` is that figure with rush
 * included; the split is scaled to it (rounding goes to sewing). No split → all of it under sewing. Replaces an old
 * one-figure "cmt". Returns a copy.
 */
export function splitLabour(split: { cut?: number; sew?: number; pack?: number } | undefined, cm: number): { cut: string; sew: string; pack: string } {
  const sum = split ? n(split.cut) + n(split.sew) + n(split.pack) : 0;
  const s2 = (v: number) => (v ? f2(v) : "");
  if (!(cm > 0)) return { cut: "", sew: "", pack: "" };
  if (!(sum > 0)) return { cut: "", sew: f2(cm), pack: "" };
  const cut = +f2(n(split!.cut) * cm / sum), pack = +f2(n(split!.pack) * cm / sum);
  return { cut: s2(cut), sew: s2(cm - cut - pack), pack: s2(pack) };
}
export function withLabour(p: AllCmParts, split: { cut?: number; sew?: number; pack?: number } | undefined, cm: number): AllCmParts {
  const out = structuredClone(p);
  out.cmt = { px: "", desc: "" };
  const l = splitLabour(split, cm);
  for (const k of ["cut", "sew", "pack"] as const) out[k] = { ...out[k], px: l[k], cny: undefined };
  return out;
}

/**
 * A new rate for the sheet: every amount typed in RMB is converted again (the RMB figure stays, the USD one moves).
 * Mutates.
 */
export function setFx(s: Sheet, fx: number): void {
  if (!(fx > 0)) return;
  s.fx = fx;
  const conv = (o: { px: string; cny?: string }) => { if (isSet(o.cny)) o.px = fromCny(o.cny, fx); };
  for (const r of [...s.fab, ...s.trim]) {
    conv(r);
    for (const t of Object.values(r.tq || {})) conv(t);
  }
  if (s.cm.parts) for (const [k] of CM_PARTS) if (s.cm.parts[k]) conv(s.cm.parts[k]);
  for (const m of s.cm.more || []) conv(m);
  for (const pr of Object.values(s.tiers?.proc || {})) {
    for (const [k, v] of Object.entries(pr.cny || {})) if (isSet(v)) pr[k as CmPartKey] = fromCny(v, fx);
  }
  for (const a of Object.values(s.add)) conv(a);
  for (const [k] of EXTRA) { const x = s.extra?.[k]; if (x && isSet(x.cny)) s[k] = fromCny(x.cny, fx); }
  for (const u of s.upcharges || []) if (isSet(u.cny)) u.amount = fromCny(u.cny, fx);
  if (isSet(s.targetCny)) s.target = fromCny(s.targetCny, fx);
  if (s.cm.parts) setCmParts(s, cmPartsOf(s));
}

/**
 * The same sheet with every amount multiplied by `k` (USD → RMB for the Excel export in RMB, JF 2026-10-13). Amounts typed
 * in RMB come out exactly as typed. Ratios (yields, coefficients, duty %) are unchanged.
 */
export function scaleMoney(src: Sheet, k: number): Sheet {
  const s = structuredClone(src);
  const m = (v: unknown, cny?: string): string => (isSet(cny) && k === fxOf(src) ? String(cny) : isSet(v) ? String(+(n(v) * k).toFixed(6)) : (v as string) ?? "");
  const mq = (v: string | number | null | undefined) => (isSet(v) ? +(n(v) * k).toFixed(6) : (v ?? null));
  for (const r of [...s.fab, ...s.trim]) {
    r.px = m(r.px, r.cny); r.q.px = mq(r.q.px);
    for (const t of Object.values(r.tq || {})) t.px = m(t.px, t.cny);
  }
  s.cm.v = m(s.cm.v); s.cm.q = mq(s.cm.q);
  if (s.cm.parts) for (const [key] of CM_PARTS) { const p = s.cm.parts[key]; if (p) p.px = m(p.px, p.cny); }
  for (const x of s.cm.more || []) x.px = m(x.px, x.cny);
  for (const t of Object.values(s.tiers?.cm || {})) t.px = m(t.px);
  for (const pr of Object.values(s.tiers?.proc || {})) for (const key of TIER_PARTS) if (pr[key] != null) pr[key] = m(pr[key], pr.cny?.[key]);
  for (const a of Object.values(s.add)) { a.px = m(a.px, a.cny); a.q = mq(a.q); }
  for (const [key] of EXTRA) { s[key] = m(s[key], s.extra?.[key]?.cny); const x = s.extra?.[key]; if (x) x.q = mq(x.q); }
  for (const u of s.upcharges || []) u.amount = m(u.amount, u.cny);
  s.other = mq(s.other as string) ?? 0; if (s.otherGp != null) s.otherGp = mq(s.otherGp) ?? "";
  s.sea = m(s.sea); s.air = m(s.air);
  if (s.target != null) s.target = m(s.target, s.targetCny);
  return s;
}

/** wash / emb: CM (factory pays) → Additional (Lever Style pays). Mutates. */
export function moveCmToAdd(s: Sheet, k: "wash" | "emb"): void {
  const p = cmPartsOf(s);
  const a = s.add[k];
  a.px = f2(perPiece(a.px, a.per, n(s.h.qty)) + n(p[k].px)); a.q = null; if (a.per) a.per = "pc";
  if (p[k].desc && !a.desc) a.desc = p[k].desc;
  p[k] = { px: "", desc: "" };
  setCmParts(s, p);
}

/** wash / emb: Additional → CM. Mutates. */
export function moveAddToCm(s: Sheet, k: "wash" | "emb"): void {
  const p = cmPartsOf(s);
  const a = s.add[k];
  const qty = n(s.h.qty);
  p[k] = { px: f2(perPiece(p[k].px, partPer(k, p[k]), qty) + perPiece(a.px, a.per, qty)), desc: p[k].desc || a.desc };
  s.add[k] = { desc: "", px: "", q: null };
  setCmParts(s, p);
}

/* ---------------- Additional: one table, one way (JF 2026-10-13) ---------------- */

/** Testing / inspection / development — the rows after the six per-piece ones; their amounts are sheet.test / insp / setup. */
export const EXTRA: [ExtraKey, string, string][] = [
  ["test", "Testing", "检测费"],
  ["insp", "Inspection", "验货费"],
  ["setup", "Development / set-up", "开发 / 制版 / 网版费"],
];

/** An amount per piece, or a whole-order total spread over the quantity. */
export const perPiece = (amount: unknown, per: Per | undefined, qty: number): number =>
  per === "order" ? (qty > 0 ? n(amount) / qty : 0) : n(amount);

export interface AddRow {
  key: AddKey | ExtraKey; en: string; zh: string; desc: string; amount: string; per: Per;
  /** per piece */ actual: number; quoted: number; hand: boolean;
  /** the amount as typed in RMB */ cny?: string;
}

/**
 * Every Additional row the same way: an amount, per piece or for the whole order, and the quote = per-piece × (1 + Additional %)
 * unless typed by hand. Sheets on the old v2 formulas (no qcoef) keep testing / inspection at cost, as they were.
 */
export function addRows(s: Sheet, c: QCoef = coefOf(s)): AddRow[] {
  const qty = n(s.h.qty);
  const rows: AddRow[] = ADD.map(([k, en, zh]) => {
    const x = s.add[k];
    const per = x.per || "pc";
    const actual = perPiece(x.px, per, qty);
    return { key: k, en, zh, desc: x.desc, amount: x.px, per, actual, quoted: isSet(x.q) ? n(x.q) : actual * (1 + c.add), hand: isSet(x.q), cny: x.cny };
  });
  for (const [k, en, zh] of EXTRA) {
    const x = s.extra?.[k] || {};
    const per = x.per || "order";
    const amount = String(s[k] ?? "");
    const actual = perPiece(amount, per, qty);
    const quoted = isSet(x.q) ? n(x.q) : s.qcoef ? actual * (1 + c.add) : actual;
    rows.push({ key: k, en, zh, desc: x.desc || "", amount, per, actual, quoted, hand: isSet(x.q), cny: x.cny });
  }
  return rows;
}

/**
 * Other upcharges row by row (JF 2026-10-13). A sheet from before has two amounts (sheet.otherGp in GP, sheet.other not in GP):
 * they show as two rows until the AM edits the table.
 */
export function upchargesOf(s: Pick<Sheet, "upcharges" | "other" | "otherGp">): Upcharge[] {
  if (s.upcharges) return s.upcharges;
  const out: Upcharge[] = [];
  if (n(s.otherGp)) out.push({ desc: "", amount: String(s.otherGp), per: "pc", gp: true });
  if (n(s.other)) out.push({ desc: "", amount: String(s.other), per: "pc", gp: false });
  return out;
}

/** Per piece: the upcharges counted in GP and those that are not. */
export function upchargeTotals(s: Sheet): { gp: number; other: number } {
  const qty = n(s.h.qty);
  let gp = 0, other = 0;
  for (const u of upchargesOf(s)) { const v = perPiece(u.amount, u.per, qty); if (u.gp) gp += v; else other += v; }
  return { gp, other };
}

export const HEADER_FIELDS: [keyof Header, string, string][] = [
  ["customer", "Customer", "客户"], ["date", "Date", "日期"], ["season", "Season", "季节"],
  ["style", "Style number", "款号"], ["name", "Style name / category", "款名 / 品类"], ["factory", "Factory", "工厂"],
  ["co", "C/O", "原产国"], ["content", "Content", "成分"], ["mill", "Fabric mill", "面料厂"],
  ["article", "Article no.", "面料编号"], ["width", "Cuttable width", "可裁幅宽"], ["qty", "Order qty (pcs)", "订单量（件）"],
];

export function newRow(kind: LineKind): LineRow {
  const base = { name: "", pos: "" };
  const rest = {
    qual: "", oem: "ODM", cat: "", px: "", yld: "", rem: "",
    src: { name: null, px: null, yld: null }, q: { px: null, fin: null, yld: null }, flag: "",
  };
  return kind === "fab" ? { ...base, unit: "m", ...rest } : { ...base, supp: "", ...rest };
}

/**
 * A fresh sheet. With `coef` the sheet uses those quote coefficients (and its default duty);
 * without it the sheet has no qcoef and computes with the v2 formulas (used by the parity tests).
 */
export function blankSheet(today = new Date().toISOString().slice(0, 10), coef?: QCoef): Sheet {
  const add = {} as Sheet["add"];
  for (const [k] of ADD) add[k] = { desc: "", px: "", q: null };
  const s: Sheet = {
    h: { customer: "", date: today, season: "", style: "", name: "", factory: "", co: "", content: "", mill: "", article: "", width: "", qty: "" },
    fab: [], trim: [], cm: { v: "", note: "", src: null, q: null, chk: "" }, add,
    test: "", insp: "", quote: false, other: 0, duty: "", sea: "", air: "", sketch: "", origin: "", bomWarn: "", from: "ai", ai: null,
  };
  if (coef) { s.qcoef = { ...coef }; if (coef.tariff != null) s.duty = String(coef.tariff); }
  return s;
}

export function hasSheet(s: Sheet): boolean {
  return !!(s.fab.length || s.trim.length || s.cm.v);
}

/** An override counts only when it holds something; "" means "use the coefficient" (偏差 D4). */
export const isSet = (v: unknown): boolean => v != null && v !== "";

const pxUp = (kind: LineKind, c: QCoef) => (kind === "fab" ? c.fabPx : c.trimPx);
const yldUp = (kind: LineKind, c: QCoef) => (kind === "fab" ? c.fabYld : c.trimYld);

export const qPx = (r: LineRow, kind: LineKind = "fab", c: QCoef = LEGACY_QCOEF): number => (isSet(r.q.px) ? n(r.q.px) : n(r.px) * (1 + pxUp(kind, c)));
export const qFin = (r: LineRow, c: QCoef = LEGACY_QCOEF): number => (isSet(r.q.fin) ? n(r.q.fin) : c.fin);
export const qYld = (r: LineRow, kind: LineKind, c: QCoef = LEGACY_QCOEF): number => (isSet(r.q.yld) ? n(r.q.yld) : n(r.yld) * (1 + yldUp(kind, c)));
/** Actual Price = Px × (1 + finActual) × Yield */
export const actPrice = (r: LineRow, c: QCoef = LEGACY_QCOEF): number => n(r.px) * (1 + c.finActual) * n(r.yld);
/** Quoted Price = Quoted Px × (1 + Finance) × Quoted Yield */
export const quoPrice = (r: LineRow, kind: LineKind, c: QCoef = LEGACY_QCOEF): number => qPx(r, kind, c) * (1 + qFin(r, c)) * qYld(r, kind, c);
export const quotedCm = (s: Sheet): number => (isSet(s.cm.q) ? n(s.cm.q) : n(s.cm.v) * coefOf(s).cmMult);
export const quotedAdd = (a: Sheet["add"][AddKey], c: QCoef = LEGACY_QCOEF): number => (isSet(a.q) ? n(a.q) : n(a.px) * (1 + c.add));

export interface Totals {
  fab: number; trim: number; qfab: number; qtrim: number; cm: number; qcm: number;
  add: number; qadd: number; oneoff: number; fob: number; qfob: number; grand: number;
  refcm: number; gp: number; gpp: number; sea: number; air: number;
}

/**
 * The sheet as totals and the Excel export see it (JF 2026-10-13, option 2): everything paid to the garment factory —
 * cut-make-pack, washing, embroidery / print, other processes — is the CM and is quoted × the CM factor; Additional is only
 * what Lever Style pays to others (× (1 + Additional %)). The CM parts become the one CM figure.
 */
export function templateSheet(s: Sheet): Sheet {
  if (!s.cm.parts && !s.cm.more?.length) return s;
  const o = structuredClone(s);
  const lines = cmLines(s);
  // unrounded: whole-order rows (customs ¥3,000 ÷ qty) are not whole cents; Excel sums the same unrounded rows (JF 2026-10-13)
  o.cm.v = lines.some((l) => isSet(l.px)) ? String(+lines.reduce((a, l) => a + l.actual, 0).toFixed(6)) : "";
  // rows with their own factor: the quoted CM is the sum of each row × its factor (unless the whole quoted CM was typed)
  if (!isSet(s.cm.q) && lines.some((l) => isSet(l.mult))) o.cm.q = lines.reduce((a, l) => a + l.actual * l.factor, 0); // unrounded, like each row's quoted price
  delete o.cm.parts; delete o.cm.more;
  return o;
}

/**
 * The original single-file tool's layout, for comparisons only: washing / embroidery under Additional, the CM = cut-make-pack.
 * (The original had no CM parts and priced washing / embroidery separately.)
 */
export function originalLayout(s: Sheet): Sheet {
  if (!s.cm.parts) return s;
  const p = cmPartsOf(s);
  const o = structuredClone(s);
  for (const k of ["wash", "emb"] as const) {
    if (!isSet(p[k].px) && !p[k].desc) continue;
    const a = o.add[k];
    a.px = isSet(a.px) ? f2(perPiece(a.px, a.per, n(o.h.qty)) + n(p[k].px)) : p[k].px;
    a.desc = [p[k].desc, a.desc].filter(Boolean).join(" · ");
  }
  // the original's CM = the factory's labour + other processes; customs & documents are new (JF 2026-10-13) and not in it
  const qty = n(o.h.qty);
  const labour = (["cmt", "cut", "sew", "pack", "other"] as const).filter((k) => isSet(p[k].px));
  o.cm.v = labour.length ? f2(labour.reduce((a, k) => a + perPiece(p[k].px, partPer(k, p[k]), qty), 0)) : "";
  delete o.cm.parts;
  return o;
}

export function totals(sheet: Sheet): Totals {
  const s = templateSheet(sheet);
  const c = coefOf(s);
  const t = { fab: 0, trim: 0, qfab: 0, qtrim: 0 } as Totals;
  s.fab.forEach((r) => { t.fab += actPrice(r, c); t.qfab += quoPrice(r, "fab", c); });
  s.trim.forEach((r) => { t.trim += actPrice(r, c); t.qtrim += quoPrice(r, "trim", c); });
  t.cm = n(s.cm.v);
  t.qcm = quotedCm(s);
  // every Additional row the same way, whole-order amounts spread over the quantity; all carry the Additional mark-up
  // (JF 2026-10-13, 偏差 D11: "else I would put them under Other upcharge")
  const rows = addRows(s, c);
  t.add = rows.reduce((a, r) => a + r.actual, 0);
  t.qadd = rows.reduce((a, r) => a + r.quoted, 0);
  t.oneoff = rows.filter((r) => r.per === "order").reduce((a, r) => a + r.actual, 0);
  t.fob = t.fab + t.trim + t.cm + t.add;
  // the other upcharge counted in GP is part of the quoted FOB (JF 2026-10-13); the one not in GP only goes into Grand Total
  const up = upchargeTotals(s);
  t.qfob = t.qfab + t.qtrim + t.qcm + t.qadd + up.gp;
  // two other upcharges (JF 2026-10-13): both go into what the customer pays; only otherGp is profit (part of Ref CM / GP)
  t.grand = t.qfob + up.other;
  t.refcm = (t.qfab - t.fab) + (t.qtrim - t.trim) + t.qcm + (t.qadd - t.add) + up.gp;
  t.gp = t.refcm - t.cm;
  t.gpp = t.grand ? t.gp / t.grand : 0;
  const duty = n(s.duty) / 100;
  t.sea = t.qfob * (1 + duty) + n(s.sea);
  t.air = t.qfob * (1 + duty) + n(s.air);
  return t;
}
